<template>
  <b-table
    hover
    :busy="busy"
    :items="item.transactions"
    :fields="fieldsList"
    responsive
    :sticky-header="'calc(100vh - 330px)'"
  >
    <!-- Column: User -->
    <template #cell(user)="{ item }">
      <b-media
        class="d-flex align-items-center pl-1 ml-25 mt-25"
        style="min-width: 150px"
      >
        <template>
          <b-link
            class="text-dark d-flex align-items-center"
            @click="fetchMembers(item.paidByMember)"
          >
            <div>
              <user-avatar 
                v-if="item.paidByMember"
                :user="item.paidByMember" 
                size="24" 
              />
              <user-avatar 
                v-else
                :user="{}" 
                size="24" 
              />
            </div>
            <div class="ml-50">
              <p v-if="item.paidByMember" class="font-weight-extrabold mb-0">
                {{ `${item.paidByMember.name} ${item.paidByMember.surname}` }}
              </p>
              <span v-else class="text-muted">---</span>
            </div>
          </b-link>
        </template>
      </b-media>
    </template>

    <!-- Column: sender -->
    <template #cell(sender)="{ item }">
      <div
        class="text-dark w-100 h-100 d-flex pl-1 ml-50 align-items-center"
        style="min-width: 150px"
      >
        <p class="mb-0">
          {{ getTranslatedName(item.emitter) }}
        </p>
      </div>
    </template>

    <!-- Column: collector -->
    <template #cell(collector)="{ item }">
      <div
        class="text-dark w-100 h-100 d-flex pl-1 ml-50 align-items-center"
        style="min-width: 150px"
      >
        <p class="mb-0">
          {{ getTranslatedName(item.collector) }}
        </p>
      </div>
    </template>

    <!-- Column: total price after discounts -->
    <template #cell(price)="{ item }">
      <div
        class="w-100 h-100 d-flex align-items-center justify-content-end"
        style="min-width: 120px"
      >
        <p class="mb-0">
          {{ item.price.toFixed(2) }}
        </p>
      </div>
    </template>

    <!-- Column: Currency -->
    <template #cell(currency)="{ item }">
      <div class="w-100 h-100 d-flex pl-1 ml-50 align-items-center">
        <p>
          {{ getCurrencyLabel(item.currency) }}
        </p>
      </div>
    </template>

    <!-- Column: Reference -->
    <template #cell(ref)="{ item }">
      <div class="w-100 h-100 d-flex pl-1 ml-50 align-items-center">
        <p>{{ item.ref }}</p>
      </div>
    </template>

    <!-- Column: Issuing Date -->
    <template #cell(date)="{ item }">
      <div
        class="w-100 h-100 d-flex pl-1 ml-50 align-items-center"
        style="min-width: 160px"
      >
        <p class="mb-0">
          {{ getTransactionDate(item.date) }}
        </p>
      </div>
    </template>

    <!-- Column: Due Date -->
    <template #cell(duedate)="{ item }">
      <div
        class="w-100 h-100 d-flex pl-1 ml-50 align-items-center"
        style="min-width: 160px"
      >
        <p class="mb-0">
          {{ getTransactionDate(item.dueDate) }}
        </p>
      </div>
    </template>

    <!-- Column: Date Creation -->
    <template #cell(createdat)="{ item }">
      <div
        class="w-100 h-100 d-flex pl-1 ml-50 align-items-center"
        style="min-width: 160px"
      >
        <p class="mb-0">
          {{ getTransactionDate(item.createdAt) }}
        </p>
      </div>
    </template>

    <!-- Column: Status -->
    <template #cell(status)="{ item }">
      <div class="d-flex align-items-center">
        <b-badge
          v-if="item.status"
          pill
          :variant="getColor(item.status)"
          class="text-capitalize mb-0 ml-1 mt-75 check-button"
        >
          {{ getStatus(item.status) }}
        </b-badge>
        <p v-else class="text-muted mb-0 ml-2 pl-75 mt-75">---</p>
      </div>
    </template>

    <!-- Column: Actions -->
    <template #cell(actions)="{ item }">
      <div class="d-flex align-items-center">
        <!-- Edit -->
        <feather-icon 
          icon="Edit2Icon" 
          size="18" 
          class="text-primary ml-2 cursor-pointer" 
          @click="handleEdit(item)" 
        />

        <!-- Delete -->
        <feather-icon 
          icon="Trash2Icon" 
          size="18" 
          class="text-primary cursor-pointer ml-1" 
          @click="handleDelete(item)" 
        />

        <!-- Download files -->
        <feather-icon
          v-if="item.fileURL"
          icon="DownloadIcon"
          size="18"
          class="text-primary cursor-pointer ml-1"
          @click="handleDownloadFiles(item)"
          :id="'download-payemnt-button-'+item.key"
        />
      </div>
    </template>
  </b-table>
</template>

<script>
import { translateTranslationTable } from "@/@core/libs/i18n/utils";
import moment from "moment";
import UserAvatar from "@core/components/user/UserAvatar.vue";
import { getCollectiveUrl } from "@core/utils/collective";
import UnshareItemModal from "@/@core/components/modal/UnshareItemModal.vue";
import ToastificationContentVue from "@/@core/components/toastification/ToastificationContent.vue";
import Service from '@/config/service-identifiers';

export default {
  components: {
    UserAvatar,
    UnshareItemModal,
  },
  props: {
    item: {
      type: Object,
      required: true,
    },
    busy: {
      type: Boolean,
      required: true,
    },
  },
  data() {
    return {
			isModalEditOpened: false,
			isDeleteModalVisible: false,
			currentItem: null,
			transaction: {},
    }
  },
  computed: {
    currentCollective() {
      return this.$store.getters.currentCollective;
    },
    fieldsList() {
      return [
        {
          key: "ref",
          label: this.$t("backoffice.transactions.fields.ref"),
          sortable: true,
        },
        {
          key: "sender",
          label: this.$t("backoffice.transactions.fields.sender"),
          sortable: true,
        },
        {
          key: "collector",
          label: this.$t("backoffice.transactions.fields.receptor"),
          sortable: true,
        },
        // {
        //   key: "community",
        //   label: this.$t("backoffice.transactions.fields.space"),
        //   sortable: true,
        // },
        {
          key: "price",
          label: this.$t("backoffice.transactions.fields.amount"),
          sortable: true,
        },
        // {
        //   key: "real",
        //   label: this.$t("backoffice.transactions.fields.real-price"),
        //   sortable: true,
        // },
        // {
        //   key: "discount",
        //   label: this.$t("backoffice.transactions.fields.discount"),
        //   sortable: true,
        // },
        {
          key: "currency",
          label: this.$t("backoffice.transactions.fields.currency"),
          sortable: true,
        },
        {
          key: "date",
          label: this.$t("backoffice.transactions.fields.issuing-date"),
          sortable: true,
        },
        {
          key: "duedate",
          label: this.$t("backoffice.transactions.fields.due-date"),
          sortable: true,
        },
        {
          key: "status",
          label: this.$t("backoffice.transactions.fields.status"),
          sortable: true,
        },
        {
          key: "user",
          label: this.$t("backoffice.transactions.fields.createdby"),
          sortable: true,
        },
        {
          key: "createdat",
          label: this.$t("backoffice.transactions.fields.createdat"),
          sortable: true,
        },
        {
          key: "actions",
          label: this.$t("backoffice.transactions.fields.actions"),
          sortable: true,
        },
      ];
    },
  },
  methods: {
    async fetchMembers(member) {
      const response = await this.$store.dispatch("getItems", {
        itemType: "communityMembers",
        forceAPICall: true,
        requestConfig: {
          page: 1,
          perPage: 10,
          username: member.username,
          getMinimalData: 1
        },
      });

      if (response.data?.length > 0) {
        const route = this.$router.resolve({
          name: "backoffice-members-details",
          params: {
            username: member.username,
            communityId: this.currentCollective.slug,
          },
        });
        window.open(route.href, "_blank");
        return;
      }
      const route = this.$router.resolve({
        name: "backoffice-members-details",
        params: {
          username: member.username,
          communityId: this.currentCollective.parentCollective.slug,
        },
      });
      window.open(route.href, "_blank");
    },
    translate(field) {
      // console.log('Trnaslating field:', field)
      return translateTranslationTable(
        this.$store.state.locale.currentLocale,
        field
      );
    },
    timestampToTime(timestamp) {
      moment.locale(this.currentLocale);
      return moment(timestamp).format("DD MMM YYYY - HH:mm");
    },
    getStatus(transaction) {
      const statusPayment = {
        pending: this.$t("backoffice.members.pending"),
        succeeded: this.$t("backoffice.members.confirmed"),
        failed: this.$t("backoffice.members.confirmed"),
      };
      return statusPayment[transaction];
    },
    getCollectiveUrl(collective) {
      return `${getCollectiveUrl(collective)}/welcome`;
    },
    getTranslatedName(item) {
      if(!item){
        return '---';
      }
      return this.translate(item.name);
    },
    getColor(transaction) {
      const color = {
        pending: "light",
        succeeded: "light-success",
        failed: "light-danger",
      };
      return color[transaction];
    },
    getTransactionDate(date){
      if(!date){
        return '---';
      }
      return this.timestampToTime(date);
    },
    getCurrencyLabel(currency) {
      if(!currency){
        return 'EUR (€)';
      }
      return currency.ISO + ' (' + currency.symbol + ')';
    },
		handleEdit(item) {
      console.log('handleEdit item', item);
			this.currentItem = { ...item };
			delete this.currentItem.key;
			delete this.currentItem.ref;
			delete this.currentItem.date;
			this.transaction = item;
			this.isModalEditOpened = true;
      // Emit edit event to parent component
      this.$emit('edit-transaction', item);
		},
		handleDelete(item) {
      console.log('handleDelete item', item);
			this.currentItem = { ...item };
			this.transaction = item;
			this.isDeleteModalVisible = true;
      // Emit delete event to parent component
      this.$emit('delete-transaction', item);
      console.log('this.currentItem', this.currentItem)
      console.log('Opening delete modal...')
		},
    async handleRemoveItem(executeDelete) {
      // console.log('handleRemoveItem...');
      const itemKey = this.currentItem?.key;
      // console.log('itemKey', itemKey);
      if (executeDelete === true) {
        // console.log('Deleting this transaction...');

        try {
          const itemData = {
            itemType: 'transactions',
            storedKey: "transactions",
            modelName: 'transaction',
            tableName: 'transactions',
            key: itemKey,
            modelKey: itemKey
          };
          // console.log('itemData', itemData)

          const deleteItemGenericResult = await this.$store.dispatch('deleteItemGeneric', {
            item: itemData,
            customName: "transactions",
            noSet: true,
          });
          // console.log('deleteItemGenericResult', deleteItemGenericResult)
          if(deleteItemGenericResult){
            // console.log('refrescando paginaaaa')
            window.location.reload();
          }

          this.$toast({
            component: ToastificationContentVue,
            props: {
              title: this.$t('backoffice.transactions.messages.delete-success'),
              icon: 'CheckIcon',
              variant: 'success',
            },
          });
        } catch {
          this.$toast({
            component: ToastificationContentVue,
            props: {
              title: this.$t('backoffice.transactions.messages.delete-error'),
              icon: 'AlertTriangleIcon',
              variant: 'danger',
            },
          });
        }

        this.$emit('load-items');
        this.$emit('close-modal', itemKey);
        this.isUnshareModalVisible = false;
			  this.currentItem = null;
      }
    },
    async handleDownloadFiles(item) {
      try {
        const response = await this.$store.$service[Service.BackendClient].get("getSignedUrl", {
          params: {
            communityKey: this.currentCollective.key,
            fileUrl: item.fileURL,
          },
        });
        if (response.data?.signedUrl) {
          window.open(response.data.signedUrl, '_blank');
        } else {
          console.error('Signed URL not found in response');
        }
      } catch (error) {
        console.error('Error fetching signed URL:', error);
      }
    },
  },
};
</script>

<style lang="scss" scoped>
@import "~@core/scss/base/bootstrap-include"; // Bootstrap includes
.totals {
  background-color: $primary;
}
.download-button {
  background-color: white !important;
  color: #28a9b8 !important;
  border: none;
}
</style>
